import { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { CalendarDays, ChevronRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Compact list of events shown under the Event Calendar. Clicking one opens
// its dedicated full-management view (Upcoming Shifts card, focused).
export const EventsListUnderCalendar = ({ monthDate }) => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);

  useEffect(() => {
    axios.get(`${API}/open-shifts?include_full=true`).then((r) => {
      const rows = (r.data || []).filter(
        (o) => (o.shift_category || 'event') !== 'retail' && !o.archived
      );
      const groups = {};
      rows.forEach((o) => {
        const key = `${o.location}|${o.position}`;
        const g = (groups[key] = groups[key] || {
          location: o.location, position: o.position, dates: [],
          staff: 0, qty: 0, hidden: o.hidden,
          accommodation: o.accommodation_provided, transport: o.transport_provided,
          geo: !!o.geo_radius, pending: false,
        });
        g.dates.push(o.date);
        g.staff += (o.claimed_by_list || []).length;
        g.qty += o.quantity || 0;
        if (!o.start_time || !o.end_time) g.pending = true;
      });
      // Window follows the month shown on the calendar: current month keeps
      // the classic "recent + upcoming" view; any other month shows the
      // events overlapping that month (so past events stay clickable).
      const m = monthDate instanceof Date && !isNaN(monthDate) ? monthDate : new Date();
      const now = new Date();
      const sameMonth = m.getFullYear() === now.getFullYear() && m.getMonth() === now.getMonth();
      const iso = (d) => d.toISOString().slice(0, 10);
      const winStart = sameMonth
        ? iso(new Date(Date.now() - 7 * 86400000))
        : `${m.getFullYear()}-${String(m.getMonth() + 1).padStart(2, '0')}-01`;
      const winEnd = sameMonth
        ? '9999-12-31'
        : iso(new Date(m.getFullYear(), m.getMonth() + 1, 0, 12));
      const list = Object.values(groups)
        .map((g) => ({ ...g, from: g.dates.sort()[0], to: g.dates[g.dates.length - 1] }))
        .filter((g) => g.to >= winStart && g.from <= winEnd)
        .sort((a, b) => a.from.localeCompare(b.from));
      setEvents(list);
    }).catch(() => {});
  }, [monthDate]);

  if (!events.length) return null;

  return (
    <Card className="bg-card border-border rounded-sm" data-testid="events-list-under-calendar">
      <CardHeader className="border-b border-border pb-3">
        <CardTitle className="text-sm font-barlow tracking-wider uppercase flex items-center gap-2">
          <CalendarDays className="w-4 h-4 text-primary" /> Events
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0 max-h-[320px] overflow-y-auto overscroll-contain">
        {events.map((ev) => (
          <button
            key={`${ev.location}|${ev.position}`}
            className="w-full flex items-center justify-between gap-3 px-4 py-3 border-b border-border/50 hover:bg-muted/50 text-left transition-colors"
            onClick={() => navigate(`/admin/events/view?name=${encodeURIComponent(ev.location)}`)}
            data-testid={`event-list-item-${ev.location}`}
          >
            <div className="min-w-0">
              <p className="text-sm font-semibold truncate">
                {ev.location} <span className="text-muted-foreground font-normal">· {ev.position}</span>
              </p>
              <p className="text-xs text-muted-foreground">
                {ev.from === ev.to ? ev.from : `${ev.from} → ${ev.to}`} · {ev.dates.length} day{ev.dates.length > 1 ? 's' : ''} · {ev.staff}/{ev.qty} staff
              </p>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {ev.hidden && <Badge variant="outline" className="rounded-sm text-[9px] uppercase text-red-400 border-red-500/50">Hidden</Badge>}
              {ev.pending && <Badge variant="outline" className="rounded-sm text-[9px] uppercase text-amber-400 border-amber-500/50">Pending times</Badge>}
              {ev.accommodation && <Badge variant="outline" className="rounded-sm text-[9px] uppercase">Accom</Badge>}
              {ev.transport && <Badge variant="outline" className="rounded-sm text-[9px] uppercase">Transport</Badge>}
              {ev.geo && <Badge variant="outline" className="rounded-sm text-[9px] uppercase">GPS</Badge>}
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            </div>
          </button>
        ))}
      </CardContent>
    </Card>
  );
};

export default EventsListUnderCalendar;
